<script lang="ts" setup>
import { computed, onMounted, Ref, ref, watch } from "vue";
import {
  IonPage,
  IonContent,
  IonInput,
  IonButton,
  alertController,
} from "@ionic/vue";
import { useFabriqStore } from "@/store/fabriq";
import { useI18n } from "@/composables/useI18n";
import storage from "@/utils/storage";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { log } from "@/utils/log";
import LoadingDiv from "@/components/ui/LoadingDiv.vue";
import AppVersion from "@/components/ui/AppVersion.vue";
import { autoLogin, initializeApp } from "@/utils/app";
import { getQueryParameters } from "@/utils/getQueryParameters";
import { AuthClient } from "@/services/auth";
import { urlOpener } from "@/utils/aws-url-opener";
import { localLogger } from "@/utils/localLogger";
import DownloadLogsButton from "@/components/tools/DownloadLogsButton.vue";
import config from "@/config";

const fabriqStore = useFabriqStore();
const futureAuthError: Ref<null | string> = ref(null);
const email = ref("");
const password = ref("");
const router = useRouter();
const wrongCredentials = ref(false);
const hasSsoLogin = computed(() => !!authConfig.value?.defaultSSOId);
const isSsoMandatory = computed(
  () => !!authConfig.value?.ssoMandatory && !!authConfig.value?.defaultSSOId
);
const urlDomainIsDifferentFromFabriqDomain = computed(() => {
  return !fabriqStore.url?.endsWith(config.domain);
});
const {
  initializationState,
  loading,
  connected,
  version,
  authConfig,
  intercomEnabled,
} = storeToRefs(fabriqStore);
fabriqStore.setInitializationState({ type: "done" });

const login = async () => {
  try {
    await performLogin();
  } catch (e) {
    await log(e);
    console.error(e);
    fabriqStore.reportSentry(new Error("perform login error", { cause: e }));
  }
};

const loginWithSSO = async () => {
  await AuthClient.logoutFuture();
  const authenticationUrl = await AuthClient.ssoAuthenticationUri();
  if (!authenticationUrl) {
    await localLogger.log("SSO Authentification error: No authentication URL");
    futureAuthError.value = "common.wrongCredentials";
    return;
  }
  urlOpener(authenticationUrl);
};

onMounted(async () => {
  email.value = await storage.get("email");
  try {
    await autoLogin(router);
    fabriqStore.setInitializationState({ type: "done" });
  } catch (e) {
    console.error(e);
    fabriqStore.reportSentry(new Error("auto login error", { cause: e }));
    fabriqStore.setInitializationState({ type: "error", error: e });
  }
  fabriqStore.setLoading(false);

  const params = getQueryParameters();
  // @ts-expect-error
  futureAuthError.value = params.error ? "common.wrongCredentials" : null;
});

const performLogin = async () => {
  if (
    !email.value ||
    !email.value.length ||
    !password.value ||
    !password.value.length
  ) {
    fabriqStore.setInitializationState({ type: "done" });
    fabriqStore.setLoading(false);
    return;
  }
  try {
    await storage.set("email", email.value);
    fabriqStore.setInitializationState({ type: "pending" });
    await fabriqStore.login(email.value, password.value);
    await initializeApp(router, true);
    fabriqStore.setLoading(false);
    fabriqStore.setInitializationState({ type: "done" });
  } catch (e) {
    log(e);
    fabriqStore.setInitializationState({ type: "error", error: e });
    fabriqStore.setLoading(false);
    wrongCredentials.value = true;
    console.error(e);
    fabriqStore.reportSentry(e as Error);
  }
};

watch(email, () => {
  wrongCredentials.value = false;
});

watch(password, (val, old) => {
  if (val === old) return;
  wrongCredentials.value = false;
});

const { t } = useI18n();

async function handleResetConfiguration() {
  const alert = await alertController.create({
    cssClass: "alert-modal",
    header: t("common.resetApplicationConfiguration"),
    message: t("common.resetApplicationConfigurationConfirmation", {
      url: fabriqStore.url,
    }),
    buttons: [
      { text: t("close"), role: "close" },
      {
        text: t("reset"),
        role: "ok",
        cssClass: "alert-modal-ok",
      },
    ],
  });
  alert.present();
  const { role } = await alert.onDidDismiss();
  if (role !== "ok") return;
  await storage.clear();
  fabriqStore.clearUrl();
  router.replace({ name: "ApplicationConfigurationIndex" });
}
</script>
<template>
  <ion-page>
    <ion-content :fullscreen="true" class="normal">
      <loading-div
        v-if="initializationState.type !== 'done' || loading || connected"
      />
      <div class="login-block" v-else>
        <div class="fabriq-logo"></div>

        <div v-if="hasSsoLogin" class="sso-container">
          <ion-button
            class="sso-button"
            expand="block"
            :loading="loading"
            mode="ios"
            color="secondary"
            fullWidth
            @click="loginWithSSO"
          >
            <font-icon name="login" material class="ion-padding" />
            {{ t("loginPage.loginWithSSO", authConfig) }}
          </ion-button>
          <template v-if="!isSsoMandatory">
            <div class="or-separator-top" />
            <div>{{ t("common.or") }}</div>
            <div class="or-separator-bottom" />
          </template>
        </div>
        <template v-if="!isSsoMandatory">
          <div class="fabriq-input-wrapper">
            <ion-input
              autofocus
              class="fabriq-input"
              inputmode="email"
              pattern="email"
              enterkeyhint="next"
              v-model="email"
              type="email"
              name="email"
              required
              :placeholder="t('common.email')"
            />
          </div>
          <div class="fabriq-input-wrapper">
            <ion-input
              v-model="password"
              class="fabriq-input"
              enterkeyhint="go"
              @keypress.enter="login"
              autofocus
              type="password"
              pattern="password"
              name="password"
              required
              :placeholder="t('common.password')"
            />
          </div>
        </template>
        <div class="hint danger" v-if="futureAuthError">
          {{ t(futureAuthError) }}
        </div>
        <div class="hint danger" v-if="wrongCredentials">
          {{ t("common.wrongCredentials") }}
        </div>
        <div class="hint warning" v-if="urlDomainIsDifferentFromFabriqDomain">
          {{
            t("login.differentUrlWarning", {
              fqdn: fabriqStore.url,
            })
          }}
        </div>
        <ion-button
          class="ion-margin-top"
          color="secondary"
          expand="block"
          @click="login"
        >
          {{ t("login.signIn") }}
        </ion-button>
        <ion-button
          class="clear-config-btn"
          fill="clear"
          size="small"
          mode="ios"
          color="secondary"
          expand="block"
          @click="handleResetConfiguration"
        >
          {{ t("common.resetApplicationConfiguration") }}
        </ion-button>
      </div>
      <p class="url-block">{{ fabriqStore.url }}</p>
      <app-version :version="version" position="absolute" />
    </ion-content>
    <download-logs-button />
    <div
      v-if="intercomEnabled"
      class="get-support-button"
      @click="fabriqStore.openIntercomChat"
    >
      {{ t("common.getSupportLite") }}
    </div>
  </ion-page>
</template>

<style scoped>
.login-block {
  position: relative;
  margin: auto;
  margin-top: 5rem;
  width: calc(100% - 84px);
  text-align: center;
}

.loading-block {
  position: relative;
  margin: auto;
  width: calc(100% - 84px);
  height: calc(100% - var(--offset-top) - var(--offset-bottom));
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.login-block .fabriq-logo {
  margin-bottom: calc(var(--ion-padding) * 4);
}

.fabriq-input-wrapper {
  text-align: left;
  font-size: var(--font-size-m);
  padding: calc(var(--ion-padding) / 3 - 2px) var(--ion-padding);
  background-color: var(--ion-color-primary-contrast);
  border: 1px solid var(--ion-border-color);
  border-radius: var(--f-border-radius);
  color: var(--ion-color-primary);
  margin-bottom: var(--ion-padding);
  margin-inline: 2px;
}

.fabriq-input-wrapper::part(native) {
  --inner-border-width: 0;
}

ion-button {
  font-size: var(--font-size-m);
  height: 2.25rem;
}

ion-button::part(native) {
  border-radius: 5px;
}

.fabriq-input {
  border: 0;
}

.clear-config-btn {
  transform: scale3d(0.9, 0.9, 0.9);
  margin: auto;
  margin-top: calc(var(--ion-padding) * 4);
}

.or-separator-top {
  margin-top: calc(var(--ion-padding) * 2);
  margin-bottom: var(--ion-padding);
  border-top: 1px solid var(--ion-border-color);
}
.or-separator-bottom {
  margin-top: var(--ion-padding);
  margin-bottom: calc(var(--ion-padding) * 2);
  border-top: 1px solid var(--ion-border-color);
}

.url-block {
  --_margin: calc(var(--ion-padding) * 4);
  position: absolute;
  bottom: var(--_margin);
  left: 0;
  right: 0;
  text-align: center;
  font-size: var(--font-size-xs);
  color: var(--ion-color-medium);
  padding: var(--ion-padding);
}
</style>
